import { useEffect, useRef } from "react";
import { useGame } from "../../app/GameProvider.js";
import { StatIcon, type StatIconName } from "../../components/StatIcon.js";
import { PATCH_NOTES, patchSummaryIcon } from "../../content/patchNotes.js";

type PatchValue =
  | { kind: "pool"; label: string }
  | { kind: "stat"; stat: Extract<StatIconName, "action" | "fatigue">; amount: number };

function patchValueLabel(value: PatchValue): string {
  if (value.kind === "pool") return value.label;
  return `${value.stat === "action" ? "行動" : "疲労"}${value.amount > 0 ? "+" : ""}${value.amount}`;
}

function PatchValueList({ values }: { values: readonly PatchValue[] }) {
  return (
    <span className="patch-value-list">
      {values.map((value) => value.kind === "pool" ? (
        <span key={`${value.kind}-${value.label}`} className="patch-pool-badge">{value.label}</span>
      ) : (
        <span key={`${value.kind}-${value.stat}`} className={`patch-stat is-${value.stat}`}>
          <StatIcon name={value.stat} />
          <b>{value.amount > 0 ? "+" : ""}{value.amount}</b>
        </span>
      ))}
    </span>
  );
}

function SummaryChange({
  change,
}: {
  change: { topic?: "event" | "stage"; name: string; title: string; description?: string };
}) {
  return (
    <>
      <img className="patch-card-icon" src={patchSummaryIcon(change.topic)} alt="" />
      <div className="patch-change-content">
        <div className="patch-change-heading">
          <strong>{change.name}</strong>
          <span className="patch-change-title">{change.title}</span>
        </div>
        {change.description ? <p className="patch-change-summary">{change.description}</p> : null}
      </div>
    </>
  );
}

export function PatchNotesDialog() {
  const { state, dispatch } = useGame();
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (state.overlay !== "patchNotes") return;
    dialogRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") dispatch({ type: "CLOSE_OVERLAY" });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state.overlay, dispatch]);

  if (state.overlay !== "patchNotes") return null;

  return (
    <div className="overlay" role="presentation" onClick={() => dispatch({ type: "CLOSE_OVERLAY" })}>
      <div
        ref={dialogRef}
        className="dialog patch-notes-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="patch-notes-title"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="patch-notes-body">
          <p className="patch-notes-kicker">PATCH NOTES</p>
          <h2 id="patch-notes-title">更新内容</h2>
          {PATCH_NOTES.map((note, index) => (
            <section key={`${note.date}-${note.title}-${index}`} className="patch-note">
              <header className="patch-note-header">
                <h3>{note.title}</h3>
                <time dateTime={note.date.replaceAll(".", "-")}>{note.date}</time>
              </header>
              <ul>
                {note.changes.map((change, changeIndex) => (
                  <li key={change.kind === "card" ? change.cardId : `${change.name}-${change.title}-${changeIndex}`}>
                    {change.kind === "card" ? (
                      <>
                        <img className="patch-card-icon" src={`/cards/${change.cardId}.png`} alt="" />
                        <div className="patch-change-content">
                          <div className="patch-change-heading">
                            <strong>{change.cardName}</strong>
                            <span className="patch-change-title">{change.title}</span>
                          </div>
                          <div
                            className="patch-change-diff"
                            aria-label={`${change.cardName}: ${patchValueLabel(change.before[0])}から${change.after.map(patchValueLabel).join("、")}へ変更`}
                          >
                            <span className="patch-change-side">
                              <small>変更前</small>
                              <PatchValueList values={change.before} />
                            </span>
                            <span className="patch-change-arrow" aria-hidden="true">→</span>
                            <span className="patch-change-side is-after">
                              <small>変更後</small>
                              <PatchValueList values={change.after} />
                            </span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <SummaryChange change={change} />
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
        <button type="button" className="btn btn-primary" onClick={() => dispatch({ type: "CLOSE_OVERLAY" })}>
          閉じる
        </button>
      </div>
    </div>
  );
}
